import { Injectable } from '@angular/core';
import { ShellClientService } from '../shell-communication/shell-client.service';
import {
   GetOrionSettingsShell,
   GetOrionSettingsShellReply,
   OrionSettingsDto,
   SaveOrionSettingsShell
} from '../shell-communication/shell-operations-protocol';
import { MessageBusService } from '../message-bus.service';
import { WEEKDAYS, Weekday } from '../adjustment-pricing-grid/copilot/apg-copilot-domain';

// CR-20260904-001, ORION Settings follow-on (2026-09-08).
//
// The SHELL-level ORION settings, loaded once at bootstrap (like ApgDefaultsService) and held here
// for every ORION on this session. Shell-level, not per-user: more than one super user may edit
// them, and every trader's ORION run applies them - super user included (owner ruling 2026-09-08:
// "all the caps must be for everyone").
//
// Published on save so an open ORION window re-reads the caps without a reload.
export const OrionSettingsUpdatedTopic = 'OrionSettingsUpdated';

// The owner's defaults. Also what a shell that has never stored the settings answers.
export const DEFAULT_APG_SHOW_TOP_N = 5;
export const DEFAULT_APG_MAX_QUERY_RANGE_DAYS = 21;

// CR-20260910-001 (leader ruling L9). Maven's two shell-level settings, mirroring the backend
// constants in ProtocolHandlers/Orion/OrionSettings.cs. Kept in step BY HAND, as the two APG
// numbers above already are.
export const DEFAULT_MAVEN_NEARBY_REPORTS_WINDOW_TRADING_DAYS = 2;
export const DEFAULT_MAVEN_RUN_BUDGET_SECONDS = 120;

// Maven (owner 2026-09-14): the most result cards Maven draws. Maven's OWN value - it used to
// borrow apgShowTopN. Mirrors OrionSettings.DefaultMavenShowTopN.
export const DEFAULT_MAVEN_SHOW_TOP_N = 5;

// Maven (owner 2026-09-14): the widest date range a Maven query may span, in calendar days from
// its start. Maven's OWN cap - it used to borrow apgMaxQueryRangeDays. Mirrors
// OrionSettings.DefaultMavenMaxQueryRangeDays.
export const DEFAULT_MAVEN_MAX_QUERY_RANGE_DAYS = 21;

// CR-20260921-003 (owner rulings 2026-09-21): "Max Results for the Expiration" - how many result
// cards ONE expiration may contribute; Maven ranks every hedge pair of every day in one list by
// credit per day and keeps at most this many per date. Mirrors
// OrionSettings.DefaultMavenMaxResultsPerExpiration.
export const DEFAULT_MAVEN_MAX_RESULTS_PER_EXPIRATION = 2;

// CR-20260915-002: "Weekdays to exclude" - the STARTING weekday-exclusion set of each date block.
// The APG ORION's is Monday (the old hard-coded preset, BR-D1); Maven's OWN is NONE (the
// stakeholders ruled Maven must not exclude Monday, 2026-09-15). Mirror
// OrionSettings.DefaultApgWeekdayExclusions / DefaultMavenWeekdayExclusions.
//
// ⚠ Handed out BY REFERENCE (never mutate): the hosts bind the resolved array straight into the
// date block's [defaultWeekdayExclusions] input, and a fresh array per read would re-fire that
// input's setter on every change-detection pass.
export const DEFAULT_APG_WEEKDAY_EXCLUSIONS: Weekday[] = ['Mon'];
export const DEFAULT_MAVEN_WEEKDAY_EXCLUSIONS: Weekday[] = [];

@Injectable({ providedIn: 'root' })
export class OrionSettingsService {

   constructor(
      private readonly _shellService: ShellClientService,
      private readonly _messageBus: MessageBusService
   ) {
   }

   private _settings: OrionSettingsDto = {
      apgShowTopN: DEFAULT_APG_SHOW_TOP_N,
      apgMaxQueryRangeDays: DEFAULT_APG_MAX_QUERY_RANGE_DAYS,
      // Held as NULL, not as the default number: "never written" and "written to the default
      // value" are different states on the wire, and only the first must stay absent so the
      // backend keeps resolving it. The getters below hand out the resolved number.
      mavenNearbyReportsWindowTradingDays: null,
      mavenRunBudgetSeconds: null,
      mavenShowTopN: null,
      mavenMaxQueryRangeDays: null,
      mavenMaxResultsPerExpiration: null,
      apgWeekdayExclusions: null,
      mavenWeekdayExclusions: null
   };

   async init(): Promise<void> {
      const start = Date.now();

      try {
         const reply = await this._shellService.processQuery<GetOrionSettingsShellReply>(new GetOrionSettingsShell());
         if (reply && reply.settings) {
            this._settings = this.sanitize(reply.settings);
         }
      } catch (e) {
         // A Shell that does not answer (e.g. one built before this operation existed) must not
         // stop the whole WebTrader from loading over two caps. The defaults apply and the failure
         // is on the console, not hidden.
         console.error('OrionSettingsService.init: could not read ORION settings, using defaults', e);
      }

      this._messageBus.publish({
         topic: 'ServiceInitialized',
         payload: {
            time: Date.now() - start,
            name: 'OrionSettings'
         }
      });
   }

   // A COPY: callers must not mutate the held values behind the dialog's back.
   get(): OrionSettingsDto {
      return { ...this._settings };
   }

   // APG ORION: the most result cards a run may offer.
   get apgShowTopN(): number {
      return this._settings.apgShowTopN;
   }

   // APG ORION: the widest date range a query may span, in calendar days from its start date.
   get apgMaxQueryRangeDays(): number {
      return this._settings.apgMaxQueryRangeDays;
   }

   // Maven: trading days EACH SIDE of a package's date that the Summary reports events for.
   // RESOLVED — a shell that never stored it answers the default, exactly as the backend does.
   get mavenNearbyReportsWindowTradingDays(): number {
      const v = this._settings.mavenNearbyReportsWindowTradingDays;
      return (typeof v === 'number' && isFinite(v)) ? v : DEFAULT_MAVEN_NEARBY_REPORTS_WINDOW_TRADING_DAYS;
   }

   // Maven: the WHOLE-RUN budget in seconds. Per-supplier orders keep their own 40 s; this is the
   // outer bound, and it is a setting rather than a constant because it is tuned on measurement.
   get mavenRunBudgetSeconds(): number {
      const v = this._settings.mavenRunBudgetSeconds;
      return (typeof v === 'number' && isFinite(v)) ? v : DEFAULT_MAVEN_RUN_BUDGET_SECONDS;
   }

   // Maven: the most result cards it draws - a DISPLAY limit on the Results column, never a cut on
   // what the run delivered. RESOLVED like the two above.
   get mavenShowTopN(): number {
      const v = this._settings.mavenShowTopN;
      return (typeof v === 'number' && isFinite(v)) ? v : DEFAULT_MAVEN_SHOW_TOP_N;
   }

   // Maven: the widest date range a query may span, in calendar days from its start date. Maven's
   // OWN cap (2026-09-14) - the Setup's Apply gate and the backend validator both read it.
   get mavenMaxQueryRangeDays(): number {
      const v = this._settings.mavenMaxQueryRangeDays;
      return (typeof v === 'number' && isFinite(v)) ? v : DEFAULT_MAVEN_MAX_QUERY_RANGE_DAYS;
   }

   // Maven: how many result cards ONE expiration may contribute (CR-20260921-003). A BACKEND cut
   // applied by the ranker - unlike mavenShowTopN, which only limits the display. RESOLVED like
   // the others; the dialog shows it, the run never reads it here (the Shell resolves its own).
   get mavenMaxResultsPerExpiration(): number {
      const v = this._settings.mavenMaxResultsPerExpiration;
      return (typeof v === 'number' && isFinite(v)) ? v : DEFAULT_MAVEN_MAX_RESULTS_PER_EXPIRATION;
   }

   // APG ORION: the weekdays its date block excludes as the STARTING POINT of a range. RESOLVED
   // (null -> Monday). The SAME array instance until the next init/save - see the note on the
   // defaults above; callers must not mutate it.
   get apgWeekdayExclusions(): Weekday[] {
      const v = this._settings.apgWeekdayExclusions;
      return v ? (v as Weekday[]) : DEFAULT_APG_WEEKDAY_EXCLUSIONS;
   }

   // Maven: its OWN starting weekday-exclusion set, independent of the APG ORION's. RESOLVED
   // (null -> none). Same reference contract as above.
   get mavenWeekdayExclusions(): Weekday[] {
      const v = this._settings.mavenWeekdayExclusions;
      return v ? (v as Weekday[]) : DEFAULT_MAVEN_WEEKDAY_EXCLUSIONS;
   }

   // Super user only - the Shell refuses anyone else. Values are stored AS ENTERED (no floor, no
   // ceiling: owner ruling 2026-09-08), so the dialog is the only place that shapes them.
   async save(settings: OrionSettingsDto): Promise<void> {
      const next = this.sanitize(settings);
      await this._shellService.processCommand(new SaveOrionSettingsShell(next));
      this._settings = next;
      this._messageBus.publish({
         topic: OrionSettingsUpdatedTopic,
         payload: { ...next }
      });
   }

   // A number that is not a number falls back to the default; that is the ONLY shaping done here.
   private sanitize(s: OrionSettingsDto): OrionSettingsDto {
      return {
         apgShowTopN: this.intOr(s.apgShowTopN, DEFAULT_APG_SHOW_TOP_N),
         apgMaxQueryRangeDays: this.intOr(s.apgMaxQueryRangeDays, DEFAULT_APG_MAX_QUERY_RANGE_DAYS),
         // ⚠ NULL PASSES THROUGH AS NULL. The two APG fields fall back to their default because
         // they have always been written; Maven's two must be able to stay UNWRITTEN, or a save
         // of the dialog would store a value nobody typed and permanently end the backend's own
         // fallback. intOrNull keeps "cleared" distinct from "set to the default".
         mavenNearbyReportsWindowTradingDays: this.intOrNull(s.mavenNearbyReportsWindowTradingDays),
         mavenRunBudgetSeconds: this.intOrNull(s.mavenRunBudgetSeconds),
         mavenShowTopN: this.intOrNull(s.mavenShowTopN),
         mavenMaxQueryRangeDays: this.intOrNull(s.mavenMaxQueryRangeDays),
         mavenMaxResultsPerExpiration: this.intOrNull(s.mavenMaxResultsPerExpiration),
         // CR-20260915-002: same rule - NULL stays NULL (never written -> the default), an array
         // is kept as an array, EMPTY included ("no exclusions" is a decision).
         apgWeekdayExclusions: this.weekdaysOrNull(s.apgWeekdayExclusions),
         mavenWeekdayExclusions: this.weekdaysOrNull(s.mavenWeekdayExclusions)
      };
   }

   // Null / undefined -> null. Otherwise the entries that name one of the five weekdays
   // (case-insensitively), as the canonical names in Mon..Fri order, de-duplicated. A fresh array.
   private weekdaysOrNull(v: any): Weekday[] | null {
      if (v === null || v === undefined || !Array.isArray(v)) {
         return null;
      }
      const names = v.map(x => (typeof x === 'string' ? x.trim().toLowerCase() : ''));
      return WEEKDAYS.filter(wd => names.indexOf(wd.toLowerCase()) >= 0);
   }

   private intOrNull(v: any): number | null {
      if (v === null || v === undefined || v === '') {
         return null;
      }
      const n = typeof v === 'number' ? v : parseInt(v, 10);
      return isFinite(n) ? Math.trunc(n) : null;
   }

   private intOr(v: any, fallback: number): number {
      const n = typeof v === 'number' ? v : parseInt(v, 10);
      return isFinite(n) ? Math.trunc(n) : fallback;
   }
}
